import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowRight, Clock, MapPin, CheckCircle,
  Home as HomeIcon, HandHeart, LayoutDashboard, Gamepad2, TrendingUp,
  Sparkles, Users, Sprout, GitBranch, Crown,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HomeDemoGames from '../components/HomeDemoGames';
import MobileStickyCTA from '../components/MobileStickyCTA';
import { useLang } from '../contexts/LanguageContext';

const Home = () => {
  const { t } = useLang();
  // The final CTA section is referenced so the mobile sticky CTA can hide
  // itself when this section enters the viewport (avoids duplicate buttons).
  const finalCtaRef = useRef(null);

  const pricingPlans = [
    {
      name: t("Cours à l'unité"),
      price: '90 CHF',
      duration: t('1 heure'),
      features: [t('À domicile'), t('Évaluation du niveau'), t('Conseil personnalisé'), t('Sans engagement')],
      lessonType: 'Cours individuel 1h',
    },
    {
      name: t('Pack 5 leçons'),
      price: '425 CHF',
      duration: t('5 heures'),
      features: [t('À domicile'), t('Suivi personnalisé'), t('Priorité planning'), t('Économie de 25 CHF')],
      popular: true,
      lessonType: 'Pack 5 leçons individuelles',
    },
    {
      name: t('Programme dix cours'),
      price: '800 CHF',
      duration: t('10 heures'),
      features: [t('À domicile'), t('Programme structuré'), t('Ressource exclusive'), t('Économie de 100 CHF')],
      lessonType: 'Pack 10 leçons individuelles',
    },
  ];

  const methodBlocks = [
    { icon: HomeIcon,         title: t('Cours privés à domicile'),  desc: t("Des cours adaptés au niveau, au rythme et aux objectifs de chaque élève.") },
    { icon: HandHeart,        title: t('Suivi personnalisé'),       desc: t("Conseils, ressources et accompagnement entre les séances.") },
    { icon: LayoutDashboard,  title: t('Espace élève privé'),       desc: t("Planning, progression, partitions, enregistrements et contenus accessibles en ligne.") },
    { icon: Gamepad2,         title: t('Entraînement interactif'),    desc: t("Apprendre les accords, le rythme et la théorie de manière ludique.") },
    { icon: TrendingUp,       title: t('Progression claire'),       desc: t("Un parcours évolutif avec objectifs concrets et motivation constante.") },
  ];

  // Concrete progression milestones — turns "how long until I play?" anxiety
  // into a visible, motivating roadmap. Premium look matching Méthode.
  const progressionMilestones = [
    {
      icon: Sprout,
      label: t('Après 1 mois'),
      title: t('Vos premiers accords'),
      desc: t("Les bases solides : posture, premiers accords ouverts, rythmiques simples. Vous jouez déjà vos premières mélodies."),
    },
    {
      icon: GitBranch,
      label: t('Après 3 mois'),
      title: t('Votre première chanson'),
      desc: t("Enchaînements fluides, lecture rythmique, vocabulaire enrichi. Vous interprétez votre première chanson de bout en bout."),
    },
    {
      icon: Crown,
      label: t('Après 6 mois'),
      title: t('Un répertoire personnel'),
      desc: t("Style affirmé, autonomie sur de nouveaux morceaux, premières improvisations. Vous construisez un répertoire qui vous ressemble."),
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF7] pb-20 md:pb-0">
      <Header />

      {/* 1. Hero Section — full-bleed warm photo with overlay + beige text */}
      <section
        className="relative overflow-hidden pt-28 sm:pt-32 pb-16 sm:pb-24 min-h-[88vh] flex items-center"
        data-testid="hero-section"
      >
        {/* Background image — guitar on linen sofa, surf house, golden hour.
            Subtle slow Ken-Burns zoom (~10s) gives a quiet cinematic life
            to the still frame without feeling animated.
            object-position keeps the guitar centred on every viewport. */}
        <img
          src="/images/anthony-hero.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-[55%_center] sm:object-center hero-kenburns will-change-transform"
        />
        {/*
          Premium directional overlay — three smooth stops that emulate the
          falloff of a real photographic graduated filter. The text column
          sits in the deepest part; the right side keeps the photo's
          golden warmth visible.
        */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(98deg, rgba(8,5,2,0.86) 0%, rgba(10,6,3,0.66) 22%, rgba(12,7,3,0.42) 42%, rgba(14,9,4,0.20) 62%, rgba(14,9,4,0.06) 80%, rgba(14,9,4,0) 100%)',
          }}
          aria-hidden="true"
        />
        {/* Faint vertical fade for cinematic depth */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.18) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.22) 100%)',
          }}
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <div className="max-w-2xl">
            <div className="space-y-10 sm:space-y-12">
              <div className="space-y-7 sm:space-y-8">
                <p
                  className="uppercase text-xs sm:text-sm font-light tracking-[0.22em]"
                  style={{ color: '#E9C77B' }}
                >
                  {t('Leçons privées à domicile · Genève')}
                </p>
                <h1
                  className="!font-['Georgia'] !font-medium !text-[44px] sm:!text-[56px] !leading-[1.1] md:!text-[68px] lg:!text-[82px] lg:!leading-[1.08]"
                  style={{ color: '#F5F1E8', textShadow: '0 2px 26px rgba(0,0,0,0.6)' }}
                >
                  {t("L'art de la guitare,")}
                  <span className="block mt-3" style={{ color: '#E9C77B' }}>
                    {t('à votre rythme.')}
                  </span>
                </h1>
              </div>
              <div className="space-y-4 max-w-2xl">
                <p
                  className="!font-['Georgia'] !font-light !text-[17px] sm:!text-[19px] leading-[1.75]"
                  style={{ color: '#FFFDF5', textShadow: '0 1px 16px rgba(0,0,0,0.7)' }}
                >
                  {t("Cours particuliers de guitare à domicile à Genève — pour adultes, adolescents et enfants motivés. Débutant ou confirmé, je construis avec vous un parcours sur mesure, exigeant et inspirant.")}
                  <span className="block mt-2" style={{ color: 'rgba(255,253,245,0.95)' }}>
                    {t('Adultes débutants bienvenus.')}
                  </span>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 pt-2 sm:pt-4">
                <Link to="/booking" className="w-full sm:w-auto group">
                  <Button
                    size="lg"
                    data-testid="hero-cta-primary"
                    className="w-full sm:w-auto px-12 h-14 rounded-sm font-medium tracking-wide gap-2.5 transition-all duration-300 ease-out border-0 hover:-translate-y-1 group-hover:[box-shadow:0_28px_60px_-14px_rgba(0,0,0,0.7),inset_0_1px_0_rgba(255,255,255,0.25)]"
                    style={{
                      background: 'linear-gradient(135deg, #D4A574 0%, #B58953 100%)',
                      color: '#1F1308',
                      boxShadow: '0 18px 40px -12px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.18)',
                    }}
                  >
                    {t('Commencer la guitare')}
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Button>
                </Link>
                {/* Secondary CTA — outline style. Designed to sit on top of
                    the dark hero photograph: 1px gold border, transparent
                    fill, cream text. On hover we subtly raise the chip with
                    a thin backdrop-blur fill (matches the trust-row pills
                    just below) so the two surfaces feel related. No
                    gradients, no glow, no transform other than -y-1. */}
                <Link to="/tarifs#premium" className="w-full sm:w-auto group">
                  <Button
                    size="lg"
                    variant="outline"
                    data-testid="hero-cta-secondary"
                    className="w-full sm:w-auto px-10 h-14 rounded-sm font-light tracking-wide gap-2.5 bg-transparent border transition-all duration-300 ease-out hover:-translate-y-1"
                    style={{
                      borderColor: 'rgba(233, 199, 123, 0.55)',
                      color: '#FFFDF5',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(20, 12, 6, 0.42)';
                      e.currentTarget.style.borderColor = 'rgba(233, 199, 123, 0.9)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.borderColor = 'rgba(233, 199, 123, 0.55)';
                    }}
                  >
                    Découvrir l&apos;espace Premium
                    <Sparkles
                      className="w-4 h-4 transition-transform duration-300 group-hover:scale-110"
                      style={{ color: '#E9C77B' }}
                    />
                  </Button>
                </Link>
              </div>
              <ul
                data-testid="hero-trust-row"
                className="flex flex-wrap gap-2 sm:gap-2.5 pt-2 sm:pt-4 text-[12px] sm:text-[12.5px] font-light"
                style={{ color: '#FFFDF5' }}
              >
                {[
                  { Icon: Clock,    label: t('Réponse sous 24 h') },
                  { Icon: MapPin,   label: t('Cours à domicile · Genève') },
                  { Icon: Sparkles, label: t('Suivi personnalisé') },
                  { Icon: Users,    label: t('Quelques créneaux disponibles') },
                ].map(({ Icon, label }, i) => (
                  <li
                    key={i}
                    className="inline-flex items-center gap-1.5 sm:gap-2 px-2.5 py-1.5 rounded-full whitespace-nowrap"
                    style={{
                      backgroundColor: 'rgba(20, 12, 6, 0.42)',
                      backdropFilter: 'blur(8px)',
                      WebkitBackdropFilter: 'blur(8px)',
                      border: '1px solid rgba(233, 199, 123, 0.22)',
                    }}
                  >
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#E9C77B' }} />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Ma méthode */}
      <section id="methode" className="py-20 md:py-28 bg-[#F6F2E8]" data-testid="methode-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12 md:mb-20 space-y-4">
            <p className="text-sm font-light text-[#D4A574] tracking-[0.2em] uppercase">{t('Ma méthode')}</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-[#5D4E37] leading-tight">
              {t('Ma méthode')}
            </h2>
            <p className="text-base md:text-xl font-light text-[#5D4E37]/75 max-w-2xl mx-auto italic">
              {t('Un apprentissage moderne, structuré et motivant.')}
            </p>
            <div className="w-16 h-[1px] bg-[#D4A574] mx-auto mt-6 md:mt-8" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-5">
            {methodBlocks.map((block, i) => {
              const Icon = block.icon;
              return (
                <div
                  key={i}
                  data-testid={`methode-block-${i}`}
                  className="group relative bg-white border border-[#EDE8DC] rounded-sm p-7 lg:p-7 flex flex-col items-start gap-3 md:gap-4 transition-all duration-300 hover:border-[#D4A574] hover:-translate-y-1 hover:shadow-[0_12px_40px_-12px_rgba(93,78,55,0.18)]"
                >
                  <span className="absolute top-5 right-5 text-[10px] font-light tracking-[0.2em] text-[#D4A574]/60">
                    0{i + 1}
                  </span>
                  <div className="w-14 h-14 rounded-full bg-[#FAF0DC] flex items-center justify-center transition-colors duration-300 group-hover:bg-[#D4A574]">
                    <Icon className="w-6 h-6 text-[#5D4E37] transition-colors duration-300 group-hover:text-white" />
                  </div>
                  <h3 className="text-lg font-serif font-medium text-[#5D4E37] leading-snug">{block.title}</h3>
                  <p className="text-sm font-light text-[#5D4E37]/75 leading-relaxed">{block.desc}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 md:mt-16 lg:mt-20 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5" data-testid="methode-cta">
            <Link to="/booking" className="w-full sm:w-auto">
              <Button
                size="lg"
                data-testid="methode-cta-primary"
                className="w-full sm:w-auto bg-[#5D4E37] hover:bg-[#6B5D4F] text-white px-8 h-12 rounded-sm font-light tracking-wide gap-2 transition-all duration-300 hover:shadow-[0_12px_32px_-10px_rgba(93,78,55,0.4)] hover:-translate-y-0.5"
              >
                {t('Réserver un premier cours')}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <a
              href="#demo-jeux"
              data-testid="methode-cta-secondary"
              onClick={(e) => {
                const target = document.getElementById('demo-jeux');
                if (target) {
                  e.preventDefault();
                  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  return;
                }
                e.preventDefault();
                window.location.href = '/mini-jeux';
              }}
              className="w-full sm:w-auto inline-flex items-center justify-center h-12 px-8 rounded-sm font-light tracking-wide text-[#5D4E37] bg-transparent border border-[#5D4E37]/25 hover:border-[#D4A574] hover:text-[#D4A574] transition-all duration-300 hover:-translate-y-0.5"
            >
              {t('Essayer l\'entraînement')}
            </a>
          </div>
        </div>
      </section>

      {/* 3. Progression / résultats élèves */}
      <section className="py-20 md:py-28 bg-white" data-testid="progression-section">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12 md:mb-16 space-y-3">
            <p className="text-sm font-light text-[#D4A574] tracking-[0.2em] uppercase">{t('Progression')}</p>
            <h2 className="text-3xl md:text-5xl font-serif font-medium text-[#5D4E37] leading-tight">
              {t('Vos premiers résultats')}
            </h2>
            <p className="text-base md:text-lg font-light text-[#5D4E37]/75 max-w-2xl mx-auto leading-relaxed">
              {t('Une progression jalonnée, du premier accord à votre répertoire personnel.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-7 relative">
            {/* Subtle horizontal connecting line on desktop only — visualises a journey */}
            <div className="hidden md:block absolute top-[44px] left-[15%] right-[15%] h-px bg-gradient-to-r from-transparent via-[#D4A574]/40 to-transparent pointer-events-none" />
            {progressionMilestones.map((m, i) => {
              const Icon = m.icon;
              return (
                <div
                  key={i}
                  data-testid={`progression-milestone-${i}`}
                  className="relative bg-[#FAFAF7] border border-[#EDE8DC] rounded-sm p-6 md:p-8 flex flex-col items-center text-center gap-3 md:gap-4 transition-all duration-300 hover:border-[#D4A574] hover:-translate-y-1 hover:shadow-[0_12px_40px_-12px_rgba(93,78,55,0.15)]"
                >
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-[#D4A574]/30 flex items-center justify-center relative z-10">
                    <Icon className="w-7 h-7 text-[#D4A574]" />
                  </div>
                  <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#D4A574]">{m.label}</p>
                  <h3 className="text-xl font-serif font-medium text-[#5D4E37] leading-snug">{m.title}</h3>
                  <p className="text-sm font-light text-[#5D4E37]/75 leading-relaxed">{m.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 4. Disponibilité — minimal premium notice (replaces testimonials) */}
      <section className="py-16 md:py-20 bg-[#FAFAF7]" data-testid="availability-notice-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#D4A574] mb-4">
            {t('Disponibilité')}
          </p>
          <p
            className="!font-['Georgia'] !font-light text-[#5D4E37]/90 text-base md:text-[19px] leading-[1.85]"
          >
            {t("La plupart des créneaux après l'école sont rapidement réservés.")}
            <span className="block mt-2 text-[#5D4E37]/75">
              {t("N'hésitez pas à consulter les disponibilités directement en ligne.")}
            </span>
          </p>
        </div>
      </section>

      {/* 5. Apprendre avec plaisir (Philosophy) */}
      <section className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="aspect-[4/3] rounded-sm overflow-hidden image-overlay shadow-xl">
                <img
                  src="/images/anthony-sofa.jpg"
                  alt="Guitare acoustique posée sur un canapé en lin, ambiance maison chaleureuse"
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </div>
            <div className="space-y-6 md:space-y-8 order-1 lg:order-2">
              <div className="space-y-3 md:space-y-4">
                <p className="text-sm font-light text-[#D4A574] tracking-widest uppercase">{t('Philosophie')}</p>
                <h2 className="text-3xl md:text-5xl font-serif font-medium text-[#5D4E37] leading-tight">
                  {t('Apprendre avec plaisir')}
                </h2>
              </div>
              <p className="text-base font-light text-[#5D4E37]/80 leading-relaxed">
                {t("Chaque élève entretient un lien unique avec la musique. Mon rôle est de vous guider avec méthode, écoute et exigence, afin de construire un parcours qui vous ressemble.")}
              </p>
              <div className="space-y-3 md:space-y-4 pt-2 md:pt-4">
                {[
                  t("Plus de 10 ans d'expérience"),
                  t('Classique, acoustique, électrique'),
                  t('Enfants dès 5 ans et adultes'),
                  t('Accessible à tous niveaux'),
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4A574] flex-shrink-0" />
                    <span className="text-sm font-light text-[#5D4E37]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. Tarifs / Formules simples */}
      <section id="tarifs" className="py-20 md:py-24 bg-[#FAFAF7]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12 md:mb-16 space-y-3 md:space-y-4">
            <p className="text-sm font-light text-[#D4A574] tracking-widest uppercase">{t('Tarifs')}</p>
            <h2 className="text-3xl md:text-5xl font-serif font-medium text-[#5D4E37]">
              {t('Formules simples')}
            </h2>
            <p className="text-base font-light text-[#5D4E37]/70 max-w-2xl mx-auto">
              {t('Cours individuels, collectifs et packs avantageux')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-8 max-w-5xl mx-auto mb-10 md:mb-12">
            {pricingPlans.slice(0, 3).map((plan, index) => (
              <Card
                key={index}
                className={`relative border transition-all duration-300 rounded-sm flex flex-col ${
                  plan.popular ? 'border-[#D4A574] shadow-lg' : 'border-[#EDE8DC] hover:border-[#D4A574]'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="uppercase text-xs font-medium tracking-wide !pt-[4px] !pb-[4px] !pl-[8px] !pr-[8px] text-white bg-[#D4A574]">
                      {t('Le plus populaire')}
                    </span>
                  </div>
                )}
                <CardContent className="p-7 md:p-8 flex flex-col h-full">
                  <div className="flex-grow">
                    <h3 className="text-xl font-serif font-medium text-[#5D4E37] mb-2">{plan.name}</h3>
                    <div className="mb-5 md:mb-6">
                      <span className="text-4xl font-serif font-semibold text-[#D4A574]">{plan.price}</span>
                      <span className="text-sm font-light text-[#5D4E37]/60 ml-2">/ {plan.duration}</span>
                    </div>
                    <ul className="space-y-2.5 md:space-y-3 mb-6 md:mb-8">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-[#D4A574]" />
                          <span className="text-sm font-light text-[#5D4E37]/80">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <Link to={`/booking?type=${encodeURIComponent(plan.lessonType)}`} className="mt-auto">
                    <Button
                      className={`w-full h-12 rounded-sm font-light ${
                        plan.popular
                          ? 'bg-[#5D4E37] hover:bg-[#6B5D4F] text-white'
                          : 'bg-[#EDE8DC] hover:bg-[#D4A574] hover:text-white text-[#5D4E37]'
                      }`}
                    >
                      {t('Réserver maintenant')}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-8 md:mt-12">
            <Link to="/tarifs">
              <Button className="bg-[#5D4E37] hover:bg-[#6B5D4F] text-white px-10 h-12 rounded-sm font-light">
                {t('Voir tous les tarifs')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 7. CTA final */}
      <section ref={finalCtaRef} className="relative py-20 md:py-32 overflow-hidden bg-[#5D4E37]" data-testid="final-cta-section">
        <div className="relative max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="!font-['Georgia'] !font-medium !text-3xl md:!text-5xl mb-5 md:mb-6 text-white">
            {t('Commençons ensemble')}
          </h2>
          <p className="max-w-2xl !font-['Georgia'] !font-light !text-base md:!text-lg mb-7 md:mb-8 mx-auto text-white/90">
            {t('Réservez votre première leçon à domicile.')}
            <br className="hidden sm:block" />
            {t('Sans engagement, simplement le plaisir de commencer.')}
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-[#D4A574] hover:bg-[#C9A961] text-[#5D4E37] px-10 h-12 rounded-sm font-light gap-2">
              {t('Prendre rendez-vous')}
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Bonus engagement layer (mini-jeux gratuits) — kept on home for guests
          but moved AFTER the main conversion path so it doesn't dilute the funnel. */}
      <HomeDemoGames />

      {/* Smart sticky CTA — appears past 50% scroll, hides near final CTA. */}
      <MobileStickyCTA hideWhenInView={finalCtaRef} />

      <Footer />
    </div>
  );
};

export default Home;
